import {
  Card,
  CardBody,
  CardHeader,
  Heading,
  VStack,
  HStack,
  Text,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Box,
  Badge,
  Skeleton,
  Divider,
  Grid,
} from '@chakra-ui/react'
import { ResponsiveContainer, BarChart as RechartsBarChart, Bar, XAxis, YAxis, Tooltip, Legend } from 'recharts'

interface ResourceStatsCardProps {
  title: string
  total: number
  active: number
  inactive: number
  deleted: number
  newToday: number
  newThisWeek: number
  newThisMonth: number
  breakdown?: {
    type: 'cluster' | 'region' | 'user' | 'status'
    data: Array<{ label: string; count: number }>
  }
  isLoading?: boolean
}

export function ResourceStatsCard({
  title,
  total,
  active,
  inactive,
  deleted,
  newToday,
  newThisWeek,
  newThisMonth,
  breakdown,
  isLoading = false,
}: ResourceStatsCardProps) {
  const breakdownChartData = breakdown?.data.map((item) => ({
    name: item.label.length > 15 ? item.label.substring(0, 15) + '...' : item.label,
    value: item.count,
  })) || []

  return (
    <Card bg="white" borderRadius="md" boxShadow="sm">
      <CardHeader pb={2}>
        <Heading size="md">{title}</Heading>
      </CardHeader>
      <CardBody pt={0}>
        <VStack spacing={4} align="stretch">
          {/* Summary Stats */}
          <Grid templateColumns="repeat(2, 1fr)" gap={3}>
            <Stat>
              <StatLabel fontSize="xs" color="gray.600">
                Total
              </StatLabel>
              {isLoading ? (
                <Skeleton height="24px" width="60px" mt={1} />
              ) : (
                <StatNumber fontSize="xl" fontWeight="bold">
                  {total.toLocaleString()}
                </StatNumber>
              )}
            </Stat>
            <Stat>
              <StatLabel fontSize="xs" color="gray.600">
                Active
              </StatLabel>
              {isLoading ? (
                <Skeleton height="24px" width="60px" mt={1} />
              ) : (
                <StatNumber fontSize="xl" fontWeight="bold" color="green.600">
                  {active.toLocaleString()}
                </StatNumber>
              )}
            </Stat>
          </Grid>

          {/* Status Breakdown */}
          <HStack spacing={2} flexWrap="wrap">
            <Badge colorScheme="green" fontSize="2xs">
              {active} active
            </Badge>
            <Badge colorScheme="gray" fontSize="2xs">
              {inactive} inactive
            </Badge>
            {deleted > 0 && (
              <Badge colorScheme="red" fontSize="2xs" variant="outline">
                {deleted} deleted
              </Badge>
            )}
          </HStack>

          {/* New Items */}
          <Box>
            <Text fontSize="xs" color="gray.600" mb={2}>
              New Items
            </Text>
            <HStack spacing={3}>
              <VStack spacing={0} align="flex-start">
                <Text fontSize="xs" color="gray.500">
                  Today
                </Text>
                {isLoading ? (
                  <Skeleton height="16px" width="30px" />
                ) : (
                  <Text fontSize="sm" fontWeight="bold">
                    {newToday}
                  </Text>
                )}
              </VStack>
              <VStack spacing={0} align="flex-start">
                <Text fontSize="xs" color="gray.500">
                  This Week
                </Text>
                {isLoading ? (
                  <Skeleton height="16px" width="30px" />
                ) : (
                  <Text fontSize="sm" fontWeight="bold">
                    {newThisWeek}
                  </Text>
                )}
              </VStack>
              <VStack spacing={0} align="flex-start">
                <Text fontSize="xs" color="gray.500">
                  This Month
                </Text>
                {isLoading ? (
                  <Skeleton height="16px" width="30px" />
                ) : (
                  <Text fontSize="sm" fontWeight="bold">
                    {newThisMonth}
                  </Text>
                )}
              </VStack>
            </HStack>
          </Box>

          {/* Breakdown Chart */}
          {breakdown && breakdown.data.length > 0 && (
            <>
              <Divider />
              <Box>
                <Text fontSize="xs" color="gray.600" mb={2}>
                  Distribution by {breakdown.type}
                </Text>
                {isLoading ? (
                  <Skeleton height="150px" width="100%" />
                ) : (
                  <Box h="150px" w="100%">
                    <ResponsiveContainer width="100%" height="100%">
                      <RechartsBarChart data={breakdownChartData}>
                        <XAxis dataKey="name" tick={{ fontSize: 10 }} angle={-45} textAnchor="end" height={60} />
                        <YAxis tick={{ fontSize: 10 }} />
                        <Tooltip />
                        <Bar dataKey="value" fill="#3182CE" radius={[4, 4, 0, 0]} />
                      </RechartsBarChart>
                    </ResponsiveContainer>
                  </Box>
                )}
              </Box>
            </>
          )}
        </VStack>
      </CardBody>
    </Card>
  )
}
